import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  HeartIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import HealthDashboard from '../components/health-tests/HealthDashboard';
import HealthTests from './HealthTests';
import AddHealthTestModal from '../components/health-tests/AddHealthTestModal';
import AddVaccinationModal from '../components/health-tests/AddVaccinationModal';
import AddTreatmentModal from '../components/health-tests/AddTreatmentModal';
import HealthCheckupModal from '../components/health-tests/HealthCheckupModal';
import HealthRemindersModal from '../components/health-tests/HealthRemindersModal';
import HealthReportsModal from '../components/health-tests/HealthReportsModal';
import HealthChecklistModal from '../components/health-tests/HealthChecklistModal';
import { filterChecklistBirds } from '../components/health-tests/healthChecklistBirdFilter';
import CageSelect from '../components/CageSelect';
import HealthAssessmentWizard from '../components/health-tests/HealthAssessmentWizard';
import VaccinationManagement from '../components/health-tests/VaccinationManagement';
import TreatmentManagement from '../components/health-tests/TreatmentManagement';
import HealthSectionNav from '../components/health-tests/HealthSectionNav';
import HealthQuickActionsMenu from '../components/health-tests/HealthQuickActionsMenu';
import HealthInvestigationPanel from '../components/health-tests/HealthInvestigationPanel';
import SymptomInvestigationWizard from '../components/health-tests/SymptomInvestigationWizard';
import HealthWorkflowsPanel from '../components/health-tests/HealthWorkflowsPanel';
import HealthCarePlansPanel from '../components/health-tests/HealthCarePlansPanel';
import HealthCatalogPanel from '../components/health-tests/HealthCatalogPanel';
import HealthReportsPanel from '../components/health-tests/HealthReportsPanel';
import AddDiseaseModal from '../components/diseases/AddDiseaseModal';
import PageHero from '../components/ui/PageHero';
import { CreateDiseaseGroupModal } from '../components/diseases/CreateDiseaseGroupModal';
import { CreateDiseaseModal } from '../components/diseases/CreateDiseaseModal';
import {
  parseHealthSearchParams,
  buildHealthSearchParams,
  HealthSectionId,
} from '../components/health-tests/healthManagementNav';
import { healthTestsApi } from '../api/healthTests';
import { vaccinationApi } from '../api/vaccinations';
import { treatmentApi } from '../api/treatments';
import { diseasesApi, diseaseGroupsApi } from '../api/diseases';
import { medicinesApi } from '../api/medicines';
import { getBirds } from '../api/birds';
import { healthReportsApi } from '../api/healthReports';
import { healthCheckupsApi } from '../api/healthCheckups';
import { healthRemindersApi } from '../api/healthReminders';
import { healthChecklistsApi } from '../api/healthChecklists';
import { healthAssessmentsApi } from '../api/healthAssessments';
import { diseaseWorkflowsApi } from '../api/diseaseWorkflows';
import { healthAnalyticsApi } from '../api/healthAnalytics';
import { Disease, DiseaseGroup } from '../types/diseases';

const HealthManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { section, view, birdId: filterBirdId } = parseHealthSearchParams(searchParams);

  const [showAddHealthTestModal, setShowAddHealthTestModal] = useState(false);
  const [showAddVaccinationModal, setShowAddVaccinationModal] = useState(false);
  const [showAddTreatmentModal, setShowAddTreatmentModal] = useState(false);
  const [showAddDiseaseModal, setShowAddDiseaseModal] = useState(false);
  const [showCreateDiseaseModal, setShowCreateDiseaseModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showHealthCheckupModal, setShowHealthCheckupModal] = useState(false);
  const [showHealthRemindersModal, setShowHealthRemindersModal] = useState(false);
  const [showHealthReportsModal, setShowHealthReportsModal] = useState(false);
  const [showHealthChecklistModal, setShowHealthChecklistModal] = useState(false);
  const [showChecklistTypeModal, setShowChecklistTypeModal] = useState(false);
  const [showAssessmentBirdPicker, setShowAssessmentBirdPicker] = useState(false);
  const [selectedChecklistType, setSelectedChecklistType] = useState<'daily' | 'weekly' | 'monthly' | 'emergency'>('daily');
  const [selectedChecklistCage, setSelectedChecklistCage] = useState('');
  const [showHealthAssessmentWizard, setShowHealthAssessmentWizard] = useState(false);
  const [selectedBirdForAssessment, setSelectedBirdForAssessment] = useState<{ id: string; name: string } | null>(null);
  const [assessmentPickerBirdId, setAssessmentPickerBirdId] = useState('');
  const [showSymptomWizard, setShowSymptomWizard] = useState(false);
  const [investigationBird, setInvestigationBird] = useState<{ id: string; label: string } | null>(null);
  const [investigationChecklistItems, setInvestigationChecklistItems] = useState<string[]>([]);

  const [activeWorkflows, setActiveWorkflows] = useState<any[]>([]);
  const [overdueWorkflows, setOverdueWorkflows] = useState<any[]>([]);
  const [workflowStats, setWorkflowStats] = useState<any>(null);
  const [analyticsDashboard, setAnalyticsDashboard] = useState<any>(null);
  const [healthTests, setHealthTests] = useState<any[]>([]);
  const [vaccinationCount, setVaccinationCount] = useState(0);
  const [treatmentCount, setTreatmentCount] = useState(0);
  const [medicineCount, setMedicineCount] = useState(0);
  const [checkupCount, setCheckupCount] = useState(0);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [birds, setBirds] = useState<any[]>([]);
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [diseaseGroups, setDiseaseGroups] = useState<DiseaseGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const navigateSection = useCallback(
    (nextSection: HealthSectionId, nextView?: string, keepBirdFilter = true) => {
      const params = new URLSearchParams();
      if (nextSection !== 'overview') params.set('section', nextSection);
      if (nextView) params.set('view', nextView);
      if (keepBirdFilter && filterBirdId) params.set('birdId', filterBirdId);
      setSearchParams(params, { replace: true });
    },
    [filterBirdId, setSearchParams],
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [testsData, birdsData, diseasesData, diseaseGroupsData, medicinesData] = await Promise.all([
        healthTestsApi.getAll().catch(() => []),
        getBirds().catch(() => []),
        diseasesApi.getAll().catch(() => []),
        diseaseGroupsApi.getAll().catch(() => []),
        medicinesApi.getAll().catch(() => []),
      ]);

      setHealthTests(testsData);
      setBirds(birdsData);
      setDiseases(diseasesData);
      setDiseaseGroups(diseaseGroupsData);
      setMedicineCount(medicinesData.length);

      const [alertsData, statsData, vaccinations, treatments, checkups] = await Promise.all([
        healthTestsApi.getAlerts().catch(() => []),
        healthTestsApi.getStatistics().catch(() => null),
        vaccinationApi.getAll().catch(() => []),
        treatmentApi.getAll().catch(() => []),
        healthCheckupsApi.getScheduled().catch(() => []),
      ]);
      setAlerts(alertsData);
      setStatistics(statsData);
      setVaccinationCount(vaccinations.length);
      setTreatmentCount(treatments.length);
      setCheckupCount(checkups.length);

      const [activeWorkflowsData, overdueWorkflowsData, workflowStatsData, analyticsDashboardData] =
        await Promise.all([
          diseaseWorkflowsApi.getActiveWorkflows().catch(() => []),
          diseaseWorkflowsApi.getOverdueWorkflows().catch(() => []),
          diseaseWorkflowsApi.getWorkflowStatistics().catch(() => null),
          healthAnalyticsApi.getDashboard().catch(() => null),
        ]);
      setActiveWorkflows(activeWorkflowsData);
      setOverdueWorkflows(overdueWorkflowsData);
      setWorkflowStats(workflowStatsData);
      setAnalyticsDashboard(analyticsDashboardData);
    } catch (error) {
      console.error('Error loading health management data:', error);
      toast.error('Some health data failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const checklistBirds = useMemo(
    () => filterChecklistBirds(birds, selectedChecklistCage || undefined),
    [birds, selectedChecklistCage],
  );

  const navCounts = useMemo(
    () => ({
      alerts: alerts.length,
      tests: healthTests.length,
      vaccinations: vaccinationCount,
      treatments: treatmentCount,
      workflows: activeWorkflows.length + overdueWorkflows.length,
      checkups: checkupCount,
      diseases: diseases.length,
      groups: diseaseGroups.length,
      medicines: medicineCount,
    }),
    [
      alerts.length,
      healthTests.length,
      vaccinationCount,
      treatmentCount,
      activeWorkflows.length,
      overdueWorkflows.length,
      checkupCount,
      diseases.length,
      diseaseGroups.length,
      medicineCount,
    ],
  );

  const handleRunMonitoring = async () => {
    try {
      await healthTestsApi.runHealthMonitoring();
      toast.success('Health monitoring completed');
      await loadData();
    } catch {
      toast.error('Failed to run health monitoring');
    }
  };

  const handleGenerateReport = async (reportType: string, format: string) => {
    try {
      toast.loading(`Generating ${reportType} report…`, { id: 'health-report' });
      switch (reportType) {
        case 'bird':
          if (!filterBirdId) {
            toast.error('Select a bird from Birds page for individual reports', { id: 'health-report' });
            return;
          }
          await healthReportsApi.generateBirdHealthReport(filterBirdId);
          break;
        case 'flock':
          await healthReportsApi.generateFlockHealthReport();
          break;
        case 'analytics':
          await healthReportsApi.generateHealthAnalytics();
          break;
        case 'vaccination':
          await healthReportsApi.generateVaccinationReport();
          break;
        case 'treatment':
          await healthReportsApi.generateTreatmentReport();
          break;
        default:
          await healthReportsApi.generateCustomReport({
            reportType: reportType as any,
            format: format as any,
          });
      }
      const blob = await healthReportsApi.downloadReport(reportType, format);
      if (blob && blob instanceof Blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `health-report-${reportType}-${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report downloaded', { id: 'health-report' });
      } else {
        throw new Error('Invalid report response');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report', { id: 'health-report' });
    }
  };

  const quickGenerateReport = async (type: 'bird' | 'flock' | 'analytics' | 'vaccination' | 'treatment') => {
    try {
      toast.loading('Generating report…', { id: 'health-report' });
      const blob = await healthReportsApi.downloadReportAsync(
        type,
        'csv',
        filterBirdId && type === 'bird' ? { birdId: filterBirdId } : undefined,
        (progress, status) => toast.loading(`Report ${status.toLowerCase()} (${progress}%)…`, { id: 'health-report' }),
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-${type}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded', { id: 'health-report' });
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report', { id: 'health-report' });
    }
  };

  const handleChecklistTypeSelected = (type: 'daily' | 'weekly' | 'monthly' | 'emergency') => {
    setSelectedChecklistType(type);
    setShowChecklistTypeModal(false);
    setShowHealthChecklistModal(true);
  };

  const handleStartAssessmentPicker = () => {
    setAssessmentPickerBirdId(filterBirdId || birds[0]?.id || '');
    setShowAssessmentBirdPicker(true);
  };

  const handleConfirmAssessmentBird = () => {
    const bird = birds.find((b) => b.id === assessmentPickerBirdId);
    if (!bird) {
      toast.error('Select a bird');
      return;
    }
    setSelectedBirdForAssessment({
      id: bird.id,
      name: bird.bandId || bird.species?.commonName || 'Bird',
    });
    setShowAssessmentBirdPicker(false);
    setShowHealthAssessmentWizard(true);
  };

  const handleStartInvestigation = (birdId: string, birdLabel: string, checklistItemIds: string[] = []) => {
    setInvestigationBird({ id: birdId, label: birdLabel });
    setInvestigationChecklistItems(checklistItemIds);
    setShowSymptomWizard(true);
  };

  const renderContent = () => {
    switch (section) {
      case 'overview':
        return (
          <HealthDashboard
            statistics={statistics}
            alerts={alerts}
            workflowStats={workflowStats}
            onRunMonitoring={handleRunMonitoring}
            onRefresh={loadData}
            onScheduleTest={() => navigateSection('records', 'tests')}
            onHealthCheckup={() => setShowHealthCheckupModal(true)}
            onViewReports={() => navigateSection('reports')}
            onScheduleReminders={() => setShowHealthRemindersModal(true)}
            onNavigateSection={navigateSection}
            onAddTest={() => setShowAddHealthTestModal(true)}
            onStartChecklist={() => {
              setSelectedChecklistCage('');
              setShowChecklistTypeModal(true);
            }}
          />
        );
      case 'records':
        if (view === 'vaccinations') {
          return (
            <VaccinationManagement
              onAddVaccination={() => setShowAddVaccinationModal(true)}
              onRefresh={loadData}
            />
          );
        }
        if (view === 'treatments') {
          return (
            <TreatmentManagement
              onAddTreatment={() => setShowAddTreatmentModal(true)}
              onRefresh={loadData}
            />
          );
        }
        return <HealthTests embedded initialBirdId={filterBirdId || undefined} />;
      case 'care':
        if (view === 'workflows') {
          return (
            <HealthWorkflowsPanel
              activeWorkflows={activeWorkflows}
              overdueWorkflows={overdueWorkflows}
              workflowStats={workflowStats}
              birds={birds}
              onRefresh={loadData}
              onStartInvestigation={(birdId, label) => handleStartInvestigation(birdId, label)}
            />
          );
        }
        if (view === 'investigation') {
          return (
            <HealthInvestigationPanel
              birds={birds}
              onStartInvestigation={(birdId, label) => handleStartInvestigation(birdId, label)}
              onStartFromChecklist={(birdId, label, ids) => handleStartInvestigation(birdId, label, ids)}
            />
          );
        }
        return (
          <HealthCarePlansPanel
            view={view}
            onScheduleCheckup={() => setShowHealthCheckupModal(true)}
            onScheduleReminder={() => setShowHealthRemindersModal(true)}
            onStartChecklist={() => {
              setSelectedChecklistCage('');
              setShowChecklistTypeModal(true);
            }}
            onStartAssessment={handleStartAssessmentPicker}
            onInvestigateFromChecklist={(birdId, label, ids) => handleStartInvestigation(birdId, label, ids)}
          />
        );
      case 'reference':
        return (
          <HealthCatalogPanel
            view={view}
            onAddDisease={() => setShowCreateDiseaseModal(true)}
            onAddGroup={() => setShowCreateGroupModal(true)}
            onRefresh={loadData}
          />
        );
      case 'reports':
        return (
          <HealthReportsPanel
            analyticsDashboard={analyticsDashboard}
            onOpenReportModal={() => setShowHealthReportsModal(true)}
            onGenerateReport={quickGenerateReport}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHero
        title="Health Management"
        subtitle="Monitor flock health, care plans, and medical records in one place"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <HealthQuickActionsMenu
              onAddHealthTest={() => setShowAddHealthTestModal(true)}
              onAddVaccination={() => setShowAddVaccinationModal(true)}
              onAddTreatment={() => setShowAddTreatmentModal(true)}
              onStartChecklist={() => {
                setSelectedChecklistCage('');
                setShowChecklistTypeModal(true);
              }}
              onScheduleCheckup={() => setShowHealthCheckupModal(true)}
              onScheduleReminder={() => setShowHealthRemindersModal(true)}
            onStartAssessment={handleStartAssessmentPicker}
            onInvestigateSymptoms={() => navigateSection('care', 'investigation')}
            onViewReports={() => navigateSection('reports')}
              onRunMonitoring={handleRunMonitoring}
              showMonitoring={section === 'overview'}
            />
            {section === 'overview' && (
              <button type="button" onClick={handleRunMonitoring} className="btn btn-secondary focus-pop text-sm">
                <ChartBarIcon className="h-4 w-4" />
                Run monitoring
              </button>
            )}
          </div>
        }
      />

      {filterBirdId && (
        <div className="flex items-center justify-between rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm text-cyan-900">
          <span>Filtering records for bird {birds.find((b) => b.id === filterBirdId)?.bandId || filterBirdId}</span>
          <button
            type="button"
            onClick={() => navigateSection(section, view, false)}
            className="font-medium text-cyan-700 hover:text-cyan-900"
          >
            Clear filter
          </button>
        </div>
      )}

      {alerts.length > 0 && section === 'overview' && (
        <button
          type="button"
          onClick={() => navigateSection('care', 'investigation')}
          className="w-full rounded-lg border-l-4 border-yellow-400 bg-yellow-50 p-4 text-left hover:bg-yellow-100/80"
        >
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-yellow-500" />
            <p className="text-sm text-yellow-800">
              <strong>{alerts.length} health alert{alerts.length !== 1 ? 's' : ''}</strong> need attention — click to review care plans
            </p>
          </div>
        </button>
      )}

      <HealthSectionNav
        section={section}
        view={view}
        counts={navCounts}
        onSectionChange={navigateSection}
      />

      <div className="glass-panel rounded-2xl p-4 sm:p-6 focus:outline-none">{renderContent()}</div>

      {/* Modals */}
      <AddHealthTestModal
        isOpen={showAddHealthTestModal}
        onClose={() => setShowAddHealthTestModal(false)}
        onSubmit={async (testData) => {
          try {
            await healthTestsApi.create(testData as Parameters<typeof healthTestsApi.create>[0]);
            toast.success('Health test added');
            setShowAddHealthTestModal(false);
            await loadData();
          } catch {
            toast.error('Failed to add health test');
          }
        }}
        birds={birds}
      />

      <AddVaccinationModal
        isOpen={showAddVaccinationModal}
        onClose={() => setShowAddVaccinationModal(false)}
        onSubmit={async (data) => {
          try {
            await vaccinationApi.create(data);
            toast.success('Vaccination scheduled');
            setShowAddVaccinationModal(false);
            await loadData();
          } catch {
            toast.error('Failed to schedule vaccination');
          }
        }}
        birds={birds}
      />

      <AddTreatmentModal
        isOpen={showAddTreatmentModal}
        onClose={() => setShowAddTreatmentModal(false)}
        onSubmit={async (data) => {
          try {
            await treatmentApi.create(data);
            toast.success('Treatment added');
            setShowAddTreatmentModal(false);
            await loadData();
          } catch {
            toast.error('Failed to add treatment');
          }
        }}
        birds={birds}
      />

      <AddDiseaseModal
        isOpen={showAddDiseaseModal}
        onClose={() => setShowAddDiseaseModal(false)}
        onSubmit={async (data) => {
          try {
            await diseasesApi.create(data);
            toast.success('Disease added');
            setShowAddDiseaseModal(false);
            await loadData();
          } catch {
            toast.error('Failed to add disease');
          }
        }}
      />

      <CreateDiseaseModal
        isOpen={showCreateDiseaseModal}
        onClose={() => setShowCreateDiseaseModal(false)}
        onSubmit={async (data) => {
          try {
            const createData = data as Omit<Disease, 'id' | 'createdAt' | 'updatedAt' | 'group' | 'medicines'>;
            await diseasesApi.create(createData);
            toast.success('Disease created');
            setShowCreateDiseaseModal(false);
            await loadData();
          } catch {
            toast.error('Failed to create disease');
          }
        }}
        groups={diseaseGroups}
      />

      <CreateDiseaseGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onSubmit={async (data) => {
          try {
            await diseaseGroupsApi.create(data);
            toast.success('Disease group created');
            setShowCreateGroupModal(false);
            await loadData();
          } catch {
            toast.error('Failed to create disease group');
          }
        }}
      />

      <HealthCheckupModal
        isOpen={showHealthCheckupModal}
        onClose={() => setShowHealthCheckupModal(false)}
        onSubmit={async (checkupData) => {
          try {
            await healthCheckupsApi.create({
              birdId: checkupData.birdId,
              checkupDate: checkupData.checkupDate,
              priority: checkupData.priority,
              checkupTypes: checkupData.checkupTypes,
              notes: checkupData.notes,
              veterinarianName: checkupData.veterinarianName,
              clinicName: checkupData.clinicName,
              followUpRequired: checkupData.followUpRequired,
              followUpDate: checkupData.followUpDate,
            });
            toast.success('Checkup scheduled');
            setShowHealthCheckupModal(false);
            await loadData();
          } catch {
            toast.error('Failed to schedule checkup');
          }
        }}
        birds={birds}
      />

      <HealthRemindersModal
        isOpen={showHealthRemindersModal}
        onClose={() => setShowHealthRemindersModal(false)}
        onSubmit={async (reminderData) => {
          try {
            await healthRemindersApi.create({
              birdId: reminderData.birdId,
              reminderType: reminderData.reminderType,
              title: reminderData.title,
              description: reminderData.description,
              reminderDate: reminderData.reminderDate,
              frequency: reminderData.frequency,
              priority: reminderData.priority,
              notificationMethods: reminderData.notificationMethods,
              maxReminders: reminderData.maxReminders,
              metadata: reminderData.metadata,
            });
            toast.success('Reminder scheduled');
            setShowHealthRemindersModal(false);
          } catch {
            toast.error('Failed to schedule reminder');
          }
        }}
        birds={birds}
      />

      <HealthReportsModal
        isOpen={showHealthReportsModal}
        onClose={() => setShowHealthReportsModal(false)}
        onGenerateReport={handleGenerateReport}
      />

      {showHealthChecklistModal && (
        <HealthChecklistModal
          isOpen={showHealthChecklistModal}
          onClose={() => setShowHealthChecklistModal(false)}
          onSubmit={async (checklistData) => {
            try {
              const payload = {
                checklistType: checklistData.checklistType.toUpperCase(),
                checklistDate: checklistData.checklistDate,
                checklistItems: checklistData.checklistItems,
                notes: checklistData.notes,
              };
              const birdIds: string[] = checklistData.birdIds ?? [];
              if (birdIds.length === 1) {
                await healthChecklistsApi.create({ birdId: birdIds[0], ...payload });
              } else {
                await healthChecklistsApi.createBatch({ birdIds, ...payload });
              }
              toast.success(`Checklist submitted for ${birdIds.length} bird${birdIds.length === 1 ? '' : 's'}`);
              setShowHealthChecklistModal(false);
              await loadData();
            } catch {
              toast.error('Failed to submit checklist');
            }
          }}
          filteredBirds={checklistBirds}
          selectedCage={selectedChecklistCage}
          checklistType={selectedChecklistType}
        />
      )}

      {selectedBirdForAssessment && (
        <HealthAssessmentWizard
          isOpen={showHealthAssessmentWizard}
          onClose={() => {
            setShowHealthAssessmentWizard(false);
            setSelectedBirdForAssessment(null);
          }}
          onSubmit={async (assessmentData) => {
            try {
              await healthAssessmentsApi.create({
                birdId: assessmentData.birdId,
                birdName: assessmentData.birdName,
                assessmentDate: assessmentData.assessmentDate,
                assessmentSteps: assessmentData.assessmentSteps,
                notes: assessmentData.notes,
              });
              toast.success('Health assessment completed');
              setShowHealthAssessmentWizard(false);
              setSelectedBirdForAssessment(null);
              await loadData();
            } catch {
              toast.error('Failed to submit assessment');
            }
          }}
          birdId={selectedBirdForAssessment.id}
          birdName={selectedBirdForAssessment.name}
        />
      )}

      {investigationBird && (
        <SymptomInvestigationWizard
          isOpen={showSymptomWizard}
          onClose={() => {
            setShowSymptomWizard(false);
            setInvestigationBird(null);
            setInvestigationChecklistItems([]);
          }}
          birdId={investigationBird.id}
          birdLabel={investigationBird.label}
          initialChecklistItemIds={investigationChecklistItems}
          onWorkflowStarted={loadData}
        />
      )}

      {showChecklistTypeModal && (
        <div className="modal-overlay z-50 flex items-center justify-center p-4">
          <div className="modal-shell w-full max-w-md p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900">Health checklist</h3>
              <button type="button" onClick={() => setShowChecklistTypeModal(false)} className="text-slate-400 hover:text-slate-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="mb-4 text-sm text-slate-600">Filter by cage, then choose checklist type.</p>
            <div className="mb-6">
              <label htmlFor="checklistCage" className="mb-2 block text-sm font-medium text-slate-700">
                Cage
              </label>
              <CageSelect
                id="checklistCage"
                value={selectedChecklistCage}
                onChange={setSelectedChecklistCage}
                allowEmpty
                allowCreate={false}
                includeInactive
                emptyLabel={`All cages (${checklistBirds.length} birds)`}
              />
            </div>
            <div className="space-y-2">
              {(
                [
                  { type: 'daily' as const, label: 'Daily', desc: 'Basic daily assessment', icon: HeartIcon, color: 'text-green-500' },
                  { type: 'weekly' as const, label: 'Weekly', desc: 'Comprehensive weekly review', icon: ClipboardDocumentListIcon, color: 'text-blue-500' },
                  { type: 'monthly' as const, label: 'Monthly', desc: 'Detailed monthly evaluation', icon: ChartBarIcon, color: 'text-purple-500' },
                  { type: 'emergency' as const, label: 'Emergency', desc: 'Urgent assessment', icon: ExclamationTriangleIcon, color: 'text-red-500' },
                ] as const
              ).map((item) => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => handleChecklistTypeSelected(item.type)}
                  className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-3 text-left hover:bg-slate-50"
                >
                  <item.icon className={`h-5 w-5 ${item.color}`} />
                  <div>
                    <div className="font-medium text-slate-900">{item.label}</div>
                    <div className="text-sm text-slate-500">{item.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAssessmentBirdPicker && (
        <div className="modal-overlay z-50 flex items-center justify-center p-4">
          <div className="modal-shell w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-slate-900">Select bird for assessment</h3>
            <select
              value={assessmentPickerBirdId}
              onChange={(e) => setAssessmentPickerBirdId(e.target.value)}
              className="input mt-4 w-full text-sm"
            >
              <option value="">Choose bird</option>
              {birds.map((bird) => (
                <option key={bird.id} value={bird.id}>
                  {bird.bandId || bird.id} {bird.species?.commonName ? `· ${bird.species.commonName}` : ''}
                </option>
              ))}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAssessmentBirdPicker(false)} className="btn btn-secondary text-sm">
                Cancel
              </button>
              <button type="button" onClick={handleConfirmAssessmentBird} className="btn btn-primary text-sm">
                Start assessment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export function HealthTestsRedirect() {
  const [searchParams] = useSearchParams();
  const birdId = searchParams.get('birdId') || '';
  const target = buildHealthSearchParams('records', 'tests', birdId || undefined);
  return <Navigate to={`/health-management${target}`} replace />;
}

export default HealthManagement;
